// Creative Strategist Decisions tab (Task #2379, spec §54).
//
// Every strategic choice the project has made, with four things the user needs
// to trust it: the value, the rationale, where it came from (provenance), and
// what it produced. Approve / reject is the control over work that has not run
// yet — rejecting a decision parks the tasks that inherited it rather than
// letting them run on a premise the user turned down.
//
// Superseded versions are kept underneath their replacement, never overwritten.

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronRight,
  History,
  Loader2,
  Replace,
  Scale,
  X,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  CS_DECISION_SOURCE_CLASSES,
  CS_DECISION_SOURCE_LABELS,
  CS_DECISION_STATUS_CLASSES,
  CS_DECISION_STATUS_LABELS,
  CS_DECISION_TYPE_LABELS,
  CS_TASK_STATUS_LABELS,
  csCanReplaceDecision,
  csDecisionsKey,
  csFormatDateTime,
  csTasksKey,
  type CreativeStrategistDecision,
} from "@/lib/creative-strategist";

export function CreativeStrategistDecisionsTab({ projectId }: { projectId: string }) {
  const decisionsKey = csDecisionsKey(projectId);
  const { data: decisions, isLoading } = useQuery<CreativeStrategistDecision[]>({
    queryKey: [decisionsKey],
    enabled: !!projectId,
  });

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="decisions-loading">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!decisions || decisions.length === 0) {
    return (
      <Card>
        <CardContent className="py-14 text-center space-y-2" data-testid="empty-decisions">
          <Scale className="h-6 w-6 mx-auto text-muted-foreground" />
          <div className="text-sm font-medium">No decisions yet</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Personas, offers and angles this project settles on will be recorded here with
            their rationale, where they came from, and what they produced.
          </p>
        </CardContent>
      </Card>
    );
  }

  const proposed = decisions.filter((d) => d.status === "proposed").length;

  return (
    <div className="space-y-3" data-testid="decisions-tab">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground" data-testid="text-decision-count">
          {decisions.length} {decisions.length === 1 ? "decision" : "decisions"}
        </div>
        {proposed > 0 && (
          <div className="text-xs text-amber-600 dark:text-amber-400" data-testid="text-decisions-awaiting">
            {proposed} awaiting your call
          </div>
        )}
      </div>
      {decisions.map((decision) => (
        <DecisionCard
          key={decision.id}
          decision={decision}
          projectId={projectId}
          decisionsKey={decisionsKey}
        />
      ))}
    </div>
  );
}

function DecisionCard({
  decision,
  projectId,
  decisionsKey,
}: {
  decision: CreativeStrategistDecision;
  projectId: string;
  decisionsKey: string;
}) {
  const { toast } = useToast();
  const [showHistory, setShowHistory] = useState(false);
  const [showValue, setShowValue] = useState(false);
  const [replacing, setReplacing] = useState(false);

  const decide = useMutation({
    mutationFn: async (action: "approve" | "reject") => {
      const res = await apiRequest(
        "POST",
        `/api/creative-strategist/decisions/${decision.id}/${action}`,
      );
      return (await res.json()) as { blockedTaskIds?: string[] };
    },
    onSuccess: (data, action) => {
      queryClient.invalidateQueries({ queryKey: [decisionsKey] });
      queryClient.invalidateQueries({ queryKey: [csTasksKey(projectId)] });
      queryClient.invalidateQueries({
        queryKey: [`/api/creative-strategist/projects/${projectId}`],
      });
      const blocked = data?.blockedTaskIds?.length ?? 0;
      toast({
        title: action === "approve" ? "Decision approved" : "Decision rejected",
        description:
          action === "reject" && blocked > 0
            ? `${blocked} ${blocked === 1 ? "task is" : "tasks are"} on hold until this is settled.`
            : undefined,
      });
    },
    onError: (e: any) => {
      toast({
        title: "Could not record that",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  const dependents = decision.dependentTasks ?? [];
  const value = decision.value ?? {};
  const summary = decisionSummary(decision);

  return (
    <Card data-testid={`decision-card-${decision.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {CS_DECISION_TYPE_LABELS[decision.decisionType] ?? decision.decisionType}
            </div>
            <div className="text-sm font-medium mt-0.5" data-testid={`decision-title-${decision.id}`}>
              {decision.title || summary || "Untitled decision"}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge
              variant="secondary"
              className={CS_DECISION_SOURCE_CLASSES[decision.sourceType] ?? ""}
              data-testid={`decision-source-${decision.id}`}
            >
              {CS_DECISION_SOURCE_LABELS[decision.sourceType] ?? decision.sourceType}
            </Badge>
            <Badge
              variant="secondary"
              className={CS_DECISION_STATUS_CLASSES[decision.status] ?? ""}
              data-testid={`decision-status-${decision.id}`}
            >
              {CS_DECISION_STATUS_LABELS[decision.status] ?? decision.status}
            </Badge>
            <Badge variant="outline" className="text-[10px]" data-testid={`decision-version-${decision.id}`}>
              v{decision.version}
            </Badge>
          </div>
        </div>

        {decision.title && summary && summary !== decision.title ? (
          <p className="text-sm" data-testid={`decision-summary-${decision.id}`}>
            {summary}
          </p>
        ) : null}

        {decision.rationale ? (
          <p className="text-xs text-muted-foreground" data-testid={`decision-rationale-${decision.id}`}>
            {decision.rationale}
          </p>
        ) : null}

        {decision.createdObject ? (
          <div className="flex items-center gap-2 text-xs" data-testid={`decision-object-${decision.id}`}>
            <span className="text-muted-foreground">Created</span>
            {decision.createdObject.href ? (
              <Link
                href={decision.createdObject.href}
                className="inline-flex items-center gap-1 font-medium hover:underline"
                data-testid={`link-decision-object-${decision.id}`}
              >
                {decision.createdObject.objectType}
                <ArrowUpRight className="h-3 w-3" />
              </Link>
            ) : (
              <span className="font-medium">{decision.createdObject.objectType}</span>
            )}
            <span className="text-muted-foreground">
              · {CS_DECISION_SOURCE_LABELS[decision.createdObject.sourceType] ??
                decision.createdObject.sourceType}
            </span>
          </div>
        ) : null}

        {decision.status === "rejected" && dependents.length > 0 ? (
          <div
            className="rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-xs"
            data-testid={`decision-blocked-tasks-${decision.id}`}
          >
            <span className="font-medium text-red-700 dark:text-red-300">
              {dependents.length} {dependents.length === 1 ? "task is" : "tasks are"} on hold:
            </span>{" "}
            <span className="text-red-700/80 dark:text-red-300/80">
              {dependents.map((t) => t.title).join(", ")}
            </span>
          </div>
        ) : dependents.length > 0 ? (
          <div className="text-xs text-muted-foreground" data-testid={`decision-dependents-${decision.id}`}>
            Used by {dependents.length} {dependents.length === 1 ? "task" : "tasks"}:{" "}
            {dependents
              .map((t) => `${t.title} (${CS_TASK_STATUS_LABELS[t.status] ?? t.status})`)
              .join(", ")}
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span>{csFormatDateTime(decision.createdAt)}</span>
            {decision.decidedBy ? <span>by {decision.decidedBy}</span> : null}
            {Object.keys(value).length > 0 && (
              <button
                type="button"
                onClick={() => setShowValue((v) => !v)}
                className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                data-testid={`button-decision-value-${decision.id}`}
              >
                {showValue ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                Details
              </button>
            )}
            {decision.history.length > 0 && (
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                data-testid={`button-decision-history-${decision.id}`}
              >
                <History className="h-3 w-3" />
                {decision.history.length} earlier{" "}
                {decision.history.length === 1 ? "version" : "versions"}
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {csCanReplaceDecision(decision) ? (
              <Button
                size="sm"
                variant={decision.status === "rejected" ? "default" : "ghost"}
                className="h-7 px-2"
                onClick={() => setReplacing(true)}
                data-testid={`button-replace-decision-${decision.id}`}
              >
                <Replace className="h-3.5 w-3.5 mr-1" />
                {decision.status === "rejected" ? "Replace this" : "Replace"}
              </Button>
            ) : null}

            {decision.status === "proposed" ? (
              <>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2"
                disabled={decide.isPending}
                onClick={() => decide.mutate("reject")}
                data-testid={`button-reject-decision-${decision.id}`}
              >
                {decide.isPending && decide.variables === "reject" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <X className="h-3.5 w-3.5 mr-1" />
                )}
                Reject
              </Button>
              <Button
                size="sm"
                className="h-7 px-2"
                disabled={decide.isPending}
                onClick={() => decide.mutate("approve")}
                data-testid={`button-approve-decision-${decision.id}`}
              >
                {decide.isPending && decide.variables === "approve" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5 mr-1" />
                )}
                Approve
              </Button>
              </>
            ) : null}
          </div>
        </div>

        {replacing ? (
          <ReplaceDecisionDialog
            decision={decision}
            projectId={projectId}
            decisionsKey={decisionsKey}
            onClose={() => setReplacing(false)}
          />
        ) : null}

        {showValue && Object.keys(value).length > 0 && (
          <pre
            className="text-[11px] bg-muted/50 rounded-md p-3 overflow-x-auto"
            data-testid={`decision-value-${decision.id}`}
          >
            {JSON.stringify(value, null, 2)}
          </pre>
        )}

        {showHistory && decision.history.length > 0 && (
          <div className="space-y-2" data-testid={`decision-history-${decision.id}`}>
            <Separator />
            {decision.history.map((prev) => (
              <div key={prev.id} className="text-xs space-y-0.5">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    v{prev.version}
                  </Badge>
                  <Badge
                    variant="secondary"
                    className={`text-[10px] ${CS_DECISION_STATUS_CLASSES[prev.status] ?? ""}`}
                  >
                    {CS_DECISION_STATUS_LABELS[prev.status] ?? prev.status}
                  </Badge>
                  <span className="text-muted-foreground">
                    {csFormatDateTime(prev.createdAt)}
                  </span>
                </div>
                <div>{prev.title || decisionSummary(prev) || "—"}</div>
                {prev.rationale ? (
                  <div className="text-muted-foreground">{prev.rationale}</div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Replace a decision with the user's own answer.
 *
 * The point of this dialog is that rejecting is not a dead end: the previous
 * version stays readable underneath, and any task the rejection parked is
 * released the moment a replacement exists. The value is edited as JSON
 * because a decision value has no fixed shape — but it is optional, so
 * correcting only the wording never asks the user to touch JSON.
 */
function ReplaceDecisionDialog({
  decision,
  projectId,
  decisionsKey,
  onClose,
}: {
  decision: CreativeStrategistDecision;
  projectId: string;
  decisionsKey: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [title, setTitle] = useState(decision.title ?? decisionSummary(decision) ?? "");
  const [rationale, setRationale] = useState("");
  const [editValue, setEditValue] = useState(false);
  const [valueText, setValueText] = useState(() =>
    JSON.stringify(decision.value ?? {}, null, 2),
  );
  const [valueError, setValueError] = useState<string | null>(null);

  const replace = useMutation({
    mutationFn: async () => {
      let valueJson: unknown;
      if (editValue) {
        try {
          valueJson = JSON.parse(valueText);
        } catch {
          throw new Error("That value is not valid JSON.");
        }
        if (!valueJson || typeof valueJson !== "object" || Array.isArray(valueJson)) {
          throw new Error("The value must be a JSON object.");
        }
      }
      const res = await apiRequest(
        "POST",
        `/api/creative-strategist/decisions/${decision.id}/supersede`,
        {
          title: title.trim(),
          rationale: rationale.trim() || undefined,
          ...(editValue ? { valueJson } : {}),
        },
      );
      return (await res.json()) as { releasedTaskIds?: string[] };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [decisionsKey] });
      queryClient.invalidateQueries({ queryKey: [csTasksKey(projectId)] });
      queryClient.invalidateQueries({
        queryKey: [`/api/creative-strategist/projects/${projectId}`],
      });
      const released = data?.releasedTaskIds?.length ?? 0;
      toast({
        title: "Decision replaced",
        description: released
          ? `${released} ${released === 1 ? "task is" : "tasks are"} off hold and ready to run.`
          : "The earlier version is kept in this decision's history.",
      });
      onClose();
    },
    onError: (e: any) => {
      setValueError(e?.message ?? "Please try again.");
    },
  });

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-lg" data-testid={`dialog-replace-decision-${decision.id}`}>
        <DialogHeader>
          <DialogTitle>Replace this decision</DialogTitle>
          <DialogDescription>
            {CS_DECISION_TYPE_LABELS[decision.decisionType] ?? decision.decisionType} · v
            {decision.version} stays in the history.
            {decision.status === "rejected" && (decision.dependentTasks?.length ?? 0) > 0
              ? " The tasks it put on hold start again from your answer."
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`replace-title-${decision.id}`}>Your answer</Label>
            <Input
              id={`replace-title-${decision.id}`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Owners of 5–20 person painting companies"
              data-testid={`input-replace-title-${decision.id}`}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`replace-rationale-${decision.id}`}>
              Why (optional)
            </Label>
            <Textarea
              id={`replace-rationale-${decision.id}`}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              rows={2}
              placeholder="What the earlier version got wrong."
              data-testid={`textarea-replace-rationale-${decision.id}`}
            />
          </div>

          {editValue ? (
            <div className="space-y-1.5">
              <Label htmlFor={`replace-value-${decision.id}`}>Value</Label>
              <Textarea
                id={`replace-value-${decision.id}`}
                value={valueText}
                onChange={(e) => setValueText(e.target.value)}
                rows={8}
                className="font-mono text-[11px]"
                data-testid={`textarea-replace-value-${decision.id}`}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditValue(true)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`button-replace-edit-value-${decision.id}`}
            >
              Edit the underlying value too
            </button>
          )}

          {valueError ? (
            <div
              className="text-xs text-red-600 dark:text-red-400"
              data-testid={`text-replace-error-${decision.id}`}
            >
              {valueError}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`button-replace-cancel-${decision.id}`}>
            Cancel
          </Button>
          <Button
            disabled={!title.trim() || replace.isPending}
            onClick={() => {
              setValueError(null);
              replace.mutate();
            }}
            data-testid={`button-replace-submit-${decision.id}`}
          >
            {replace.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Replace className="h-3.5 w-3.5 mr-1.5" />
            )}
            Replace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One readable line out of whatever shape the decision value happens to be. */
function decisionSummary(decision: CreativeStrategistDecision): string {
  const value = (decision.value ?? {}) as Record<string, any>;
  for (const key of ["name", "label", "summary", "angleName", "personaName", "offerName"]) {
    const v = value[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}
